'use client';

import { Search, Tag } from 'lucide-react';
import { useTranslations } from 'next-intl';

import type { Suggestion } from '@website/services/suggestions.service';

export interface KeywordSuggestionsListProps {
  query: string;
  suggestions: Suggestion[];
  isLoading: boolean;
  onSelect: (item: { name: string; type?: 'category' | 'activity'; id?: string }) => void;
}

/**
 * Inner content for the keyword suggestions panel.
 * No positioning — consumers wrap this in either an absolute container or a Popover.
 */
export function KeywordSuggestionsList({ query, suggestions, isLoading, onSelect }: KeywordSuggestionsListProps) {
  const t = useTranslations('SearchPage') as any;
  const trimmedQuery = query.trim();
  const isEmptyQuery = trimmedQuery.length === 0;

  // Don't show anything when query is empty
  if (isEmptyQuery) {
    return null;
  }

  if (isLoading && suggestions.length === 0) {
    return <div className="px-4 py-3 text-sm text-neutral-500">{t('searching')}</div>;
  }

  // Filter out suggestions that exactly match the query (case-insensitive)
  // to avoid showing duplicates
  const filteredSuggestions = suggestions.filter((s) => s.name.toLowerCase() !== trimmedQuery.toLowerCase());

  return (
    <>
      {/* First item: the exact query typed by the user */}
      <button
        type="button"
        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-neutral-50 transition-colors text-left"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onSelect({ name: trimmedQuery })}
      >
        <Search className="w-4 h-4 text-neutral-400 shrink-0" />
        <span className="text-sm font-medium text-neutral-800 truncate">{trimmedQuery}</span>
      </button>

      {/* Remaining suggestions */}
      {filteredSuggestions.map((suggestion) => {
        const Icon = suggestion.type === 'category' ? Tag : Search;
        return (
          <button
            key={`${suggestion.type}:${suggestion.id}`}
            type="button"
            className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-neutral-50 transition-colors text-left"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect({ name: suggestion.name, type: suggestion.type, id: suggestion.id })}
          >
            <Icon className="w-4 h-4 text-neutral-400 shrink-0" />
            <span className="text-sm font-medium text-neutral-800 truncate">{suggestion.name}</span>
          </button>
        );
      })}
    </>
  );
}

/**
 * Absolutely-positioned dropdown panel wrapping KeywordSuggestionsList.
 * Used in HomeSearchBar where the parent has no overflow clipping.
 */
export function KeywordSuggestionsDropdown({
  className,
  ...listProps
}: KeywordSuggestionsListProps & { className?: string }) {
  return (
    <div
      className={`absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 max-h-[280px] overflow-y-auto ${className || ''}`}
    >
      <KeywordSuggestionsList {...listProps} />
    </div>
  );
}
